import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component
 * 
 * Provides comprehensive SEO meta tags for each page including:
 * - Title and description
 * - Open Graph tags (Facebook, LinkedIn, etc.)
 * - Twitter Card tags
 * - Canonical URL
 * - Structured data (JSON-LD)
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   image="/custom-image.jpg"
 * />
 */

// Default SEO values - these should match what's in the database
const defaults = {
  siteName: 'SpeechFormation',
  title: 'SpeechFormation - Speech Practice Through Play',
  description: 'Voice-controlled games that make speech practice feel like play, not therapy. Designed for children with speech delays and autism.',
  image: '/og-image.jpg',
  twitterHandle: '@speechformation',
  siteUrl: 'https://speechformation.com'
};

export default function SEO({ 
  title,
  description,
  image,
  type = 'website',
  noIndex = false,
  structuredData = null,
  keywords = null
}) {
  const location = useLocation();
  
  // Build full title
  const fullTitle = title 
    ? `${title} | ${defaults.siteName}`
    : defaults.title;
  
  // Use provided values or defaults
  const metaDescription = description || defaults.description;
  const metaImage = image?.startsWith('http') 
    ? image 
    : `${defaults.siteUrl}${image || defaults.image}`;
  const canonicalUrl = `${defaults.siteUrl}${location.pathname}`;
  
  // Default keywords
  const metaKeywords = keywords || 
    'speech therapy, autism, speech delay, voice games, speech practice, educational games, children therapy, ABA therapy, speech language pathologist';

  // Build structured data for Organization (appears on all pages)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'SpeechFormation',
    url: defaults.siteUrl,
    logo: `${defaults.siteUrl}/logo.png`,
    description: defaults.description,
    sameAs: [
      'https://twitter.com/speechformation',
      'https://facebook.com/speechformation',
      'https://linkedin.com/company/speechformation'
    ]
  };

  // Build WebSite schema for search
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'SpeechFormation',
    url: defaults.siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${defaults.siteUrl}/games?search={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={defaults.siteName} />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaults.twitterHandle} />
      <meta name="twitter:creator" content={defaults.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Additional SEO tags */}
      <meta name="author" content="SpeechFormation" />
      <meta name="application-name" content="SpeechFormation" />
      <meta name="apple-mobile-web-app-title" content="SpeechFormation" />
      
      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {/* Structured Data - WebSite */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      
      {/* Custom Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Pre-configured SEO for specific page types
 */

// For game pages
export function GameSEO({ game }) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: game.name,
    description: game.description,
    applicationCategory: 'GameApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    aggregateRating: game.rating ? {
      '@type': 'AggregateRating',
      ratingValue: game.rating,
      ratingCount: game.ratingCount
    } : undefined
  };

  return (
    <SEO
      title={game.name}
      description={game.description}
      image={game.thumbnail}
      type="article"
      structuredData={structuredData}
      keywords={`${game.name}, speech game, voice game, ${game.skillTags?.join(', ')}`}
    />
  );
}

// For pricing page
export function PricingSEO() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'SpeechFormation Premium',
    description: 'Full access to all voice-controlled speech therapy games',
    offers: [
      {
        '@type': 'Offer',
        name: 'Monthly Premium',
        price: '10.00',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock'
      }
    ]
  };

  return (
    <SEO
      title="Pricing"
      description="Affordable speech practice games. First month free, then just $10/month for full access to all games."
      structuredData={structuredData}
      keywords="speech therapy cost, speech game pricing, affordable speech therapy, speech practice subscription"
    />
  );
}

// For therapist/school/parent landing pages
export function AudienceSEO({ audience, title, description }) {
  const audienceKeywords = {
    therapists: 'speech therapist tools, SLP resources, therapy games, speech pathologist, clinical speech tools',
    schools: 'school speech program, classroom speech games, special education, IEP speech goals, school therapy',
    parents: 'speech delay help, autism speech games, home speech practice, parent speech resources, speech at home'
  };

  return (
    <SEO
      title={title}
      description={description}
      keywords={audienceKeywords[audience]}
    />
  );
}

// For blog/article pages (future use)
export function ArticleSEO({ article }) {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    author: {
      '@type': 'Person',
      name: article.author
    },
    publisher: {
      '@type': 'Organization',
      name: 'SpeechFormation',
      logo: {
        '@type': 'ImageObject',
        url: 'https://speechformation.com/logo.png'
      }
    }
  };

  return (
    <SEO
      title={article.title}
      description={article.excerpt}
      image={article.image}
      type="article"
      structuredData={structuredData}
    />
  );
}
